import { useColorModeValue } from "../Components/ui/color-mode";
import { useSchoolsContext } from "../Contexts/schoolContext";
import { useMemo } from "react";
import Lightlogo from "../assets/resultng-logo-light.png";
import Darklogo from "../assets/resultng-logo-dark.png";
import schoolPlaceholderLogoLight from "../assets/placeholder-logo-light.png";
import schoolPlaceholderLogoDark from "../assets/placeholder-logo-dark.png";

export const useThemeColors = () => {
  const { accentColor } = useSchoolsContext();

  // TOP-LEVEL HOOK CALLS
  const schoolAccentBG = useColorModeValue(
    accentColor ? `${accentColor}.200` : "brand.400",
    accentColor ? `${accentColor}.600` : "brand.600"
  );

  const schoolAccentText = useColorModeValue(
    accentColor ? `${accentColor}.600` : "brand.600",
    accentColor ? `${accentColor}.200` : "brand.200"
  );
  const schoolAccentIcon = useColorModeValue(
    accentColor ? `${accentColor}.300` : "brand.300",
    accentColor ? `${accentColor}.400` : "brand.400"
  );
  const schoolAccentHover = useColorModeValue(
    accentColor ? `${accentColor}.200/20` : "brand.200/20",
    accentColor ? `${accentColor}.950/20` : "brand.950/20"
  );
  const schoolAccentFocus = useColorModeValue(
    accentColor ? `${accentColor}.500/10` : "brand.300/30",
    accentColor ? `${accentColor}.500/10` : "brand.700/30"
  );

  const schoolAccentGlassBg = useColorModeValue(
    accentColor ? `${accentColor}.300/20` : "brand.500/20",
    accentColor ? `${accentColor}.600/20` : "brand.600/20"
  );

  const schoolAccentGlassBorder = useColorModeValue(
    accentColor ? `${accentColor}.400/30` : "brand.500/50",
    accentColor ? `${accentColor}.600/30` : "brand.600/50"
  );

  const brandButton = useColorModeValue("blue.200", "blue.700");
  const brandButtonHover = useColorModeValue("blue.300", "blue.600");
  const brandNavPrimaryButton = useColorModeValue(
    "rgba(62, 128, 204, 0.5)",
    "rgba(62, 128, 204, 0.5)"
  );
  const brandNavSecondaryButton = useColorModeValue(
    "rgba(62, 128, 204, 0.3)",
    "rgba(62, 128, 204, 0.3)"
  );
  const brandNavButtonHover = useColorModeValue(
    "rgba(62, 128, 204, 0.2)",
    "rgba(62, 128, 204, 0.2)"
  );

  const backgroundPrimary = useColorModeValue("white", "black");
  const backgroundSecondary = useColorModeValue("gray.50", "gray.950"); // large backgrounds
  const backgroundTertiary = useColorModeValue("gray.100", "gray.900");
  const backgroundSubtle = useColorModeValue("gray.100", "gray.800"); //subtle cards
  const backgroundMuted = useColorModeValue("gray.50", "gray.900"); //muted backgrounds
  const backgroundSidebar = useColorModeValue("white", "gray.700");
  const backgroundTopBar = useColorModeValue("white", "gray.900");
  const backgroundModal = useColorModeValue("white", "gray.800");
  const backgroundCard = useColorModeValue("white", "gray.900");
  const backgroundStep = useColorModeValue("gray.100", "gray.700");
  const backgroundNavBar = useColorModeValue("#0C1635", "gray.900");
  const backgroundGlass = useColorModeValue(
    "rgba(255, 255, 255, 0.6)",
    "rgba(23, 25, 35, 0.5)"
  );

  const borderPrimary = useColorModeValue("gray.200", "gray.700");
  const borderSecondary = useColorModeValue("gray.300", "gray.600");
  const borderSubtle = useColorModeValue("gray.100", "gray.700");
  const borderInput = useColorModeValue("gray.200", "gray.700");
  const borderAccent = useColorModeValue("blue.200", "blue.600");
  const borderGlass = useColorModeValue(
    "rgba(0, 0, 0, 0.1)",
    "rgba(255, 255, 255, 0.1)"
  );

  const textPrimary = useColorModeValue("gray.800", "gray.50");
  const textSecondary = useColorModeValue("gray.500", "gray.400");
  const textTertiary = useColorModeValue("gray.400", "gray.500");
  const textInverse = useColorModeValue("white", "gray.800");
  const textMuted = useColorModeValue("gray.600", "gray.300");
  const textLink = useColorModeValue("blue.600", "blue.400");
  const textLinkHover = useColorModeValue("blue.700", "blue.300");

  const interactiveHover = useColorModeValue("gray.50", "gray.800");
  const interactiveActive = useColorModeValue("gray.100", "gray.600");
  const interactiveFocus = useColorModeValue("blue.50", "blue.900");
  const interactiveDisabled = useColorModeValue("gray.100", "gray.700");
  const interactiveTableHover = useColorModeValue("gray.50", "gray.700");
  const interactiveTableSelected = useColorModeValue("blue.50", "blue.900");

  const statusSuccess = useColorModeValue("green.500", "green.400");
  const statusWarning = useColorModeValue("orange.500", "orange.400");
  const statusError = useColorModeValue("red.500", "red.400");
  const statusInfo = useColorModeValue("blue.500", "blue.400");
  const statusSuccessLight = useColorModeValue("green.50", "green.900");
  const statusWarningLight = useColorModeValue("orange.50", "orange.900");
  const statusErrorLight = useColorModeValue("red.50", "red.900");
  const statusInfoLight = useColorModeValue("blue.50", "blue.900");
  const statusSuccessBorder = useColorModeValue(
    "rgba(14, 233, 76, 0.23)",
    "rgba(14, 233, 76, 0.27)"
  );
  const statusWarningBorder = useColorModeValue(
    "rgba(233, 153, 14, 0.23)",
    "rgba(233, 153, 14, 0.27)"
  );
  const statusErrorBorder = useColorModeValue(
    "rgba(217, 64, 64, 0.23)",
    "rgba(217, 64, 64, 0.27)"
  );
  const statusInfoBorder = useColorModeValue(
    "rgba(59, 157, 199, 0.23)",
    "rgba(59, 157, 199, 0.27)"
  );

  const shadowSm = useColorModeValue(
    "rgba(0, 0, 0, 0.05)",
    "rgba(0, 0, 0, 0.3)"
  );
  const shadowMd = useColorModeValue(
    "rgba(0, 0, 0, 0.1)",
    "rgba(0, 0, 0, 0.4)"
  );
  const shadowLg = useColorModeValue(
    "rgba(0, 0, 0, 0.15)",
    "rgba(0, 0, 0, 0.5)"
  );
  const shadowCard = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );
  const shadowCardHover = useColorModeValue(
    "0 8px 25px -8px rgba(0, 0, 0, 0.15)",
    "0 8px 25px -8px rgba(0, 0, 0, 0.4)"
  );

  const overlayBackdrop = useColorModeValue(
    "rgba(255, 255, 255, 0.95)",
    "rgba(26, 32, 44, 0.95)"
  );
  const overlayModal = useColorModeValue(
    "rgba(0, 0, 0, 0.4)",
    "rgba(0, 0, 0, 0.6)"
  );
  const overlayImage = useColorModeValue("whiteAlpha.700", "blackAlpha.500");

  const utilityAlt = useColorModeValue("gray.100", "gray.700");
  const utilityScroll = useColorModeValue("gray.200", "gray.600");
  const utilityScrollBg = useColorModeValue(
    "rgba(0,0,0,0.05)",
    "rgba(255,255,255,0.05)"
  );
  const utilityTableHeader = useColorModeValue("gray.50", "gray.900");
  const utilityInputBg = useColorModeValue("gray.50", "gray.700");
  const utilityInputBorder = useColorModeValue("gray.200", "gray.600");
  const utilityPulse = useColorModeValue("red.400", "red.300");

  const logo = useColorModeValue(Lightlogo, Darklogo);
  const schoolPlaceholderLogo = useColorModeValue(
    schoolPlaceholderLogoLight,
    schoolPlaceholderLogoDark
  );

  // ===== SCHOOL-SPECIFIC COLORS =====
  const schoolAccent = useMemo(
    () => ({
      // Primary school colors
      primary: accentColor || "brand",
      secondary: accentColor ? `${accentColor}.400` : "brand.400",
      tertiary: accentColor ? `${accentColor}.600` : "brand.600",
      // Dynamic color mode variants
      icon: schoolAccentIcon,
      // Interactive states
      hover: schoolAccentHover,
      focus: schoolAccentFocus,
      // Navigation specific
      text: schoolAccentText,
      bg: schoolAccentBG,
      glassBg: schoolAccentGlassBg,
      glassBorder: schoolAccentGlassBorder,
    }),
    [
      accentColor,
      schoolAccentIcon,
      schoolAccentHover,
      schoolAccentFocus,
      schoolAccentText,
      schoolAccentBG,
      schoolAccentGlassBg,
      schoolAccentGlassBorder,
    ]
  );

  // ===== BRAND COLORS =====
  const brand = useMemo(
    () => ({
      primary: "brand",
      secondary: "blue.500",
      tertiary: "blue.600",
      // Logo and branding

      // Brand-specific interactive states
      button: brandButton,
      buttonHover: brandButtonHover,
      // Brand-specific interactive states
      navPrimaryButton: brandNavPrimaryButton,
      navSecondaryButton: brandNavSecondaryButton,
      navButtonHover: brandNavButtonHover,
    }),
    [
      brandButton,
      brandButtonHover,
      brandNavPrimaryButton,
      brandNavSecondaryButton,
      brandNavButtonHover,
    ]
  );

  // ===== BACKGROUND COLORS =====
  const background = useMemo(
    () => ({
      primary: backgroundPrimary,
      secondary: backgroundSecondary,
      tertiary: backgroundTertiary,
      subtle: backgroundSubtle,
      // Component-specific backgrounds
      sidebar: backgroundSidebar,
      topBar: backgroundTopBar,
      modal: backgroundModal,
      card: backgroundCard,
      step: backgroundStep,
      muted: backgroundMuted,
      // Special backgrounds
      navBar: backgroundNavBar,
      glass: backgroundGlass,
    }),
    [
      backgroundPrimary,
      backgroundSecondary,
      backgroundTertiary,
      backgroundSubtle,
      backgroundSidebar,
      backgroundTopBar,
      backgroundModal,
      backgroundCard,
      backgroundStep,
      backgroundNavBar,
      backgroundGlass,
      backgroundMuted,
    ]
  );

  // ===== BORDER COLORS =====
  const border = useMemo(
    () => ({
      primary: borderPrimary,
      secondary: borderSecondary,
      accent: borderAccent,
      subtle: borderSubtle,
      input: borderInput,
      // Glass borders
      glass: borderGlass,
    }),
    [borderPrimary, borderSecondary, borderAccent, borderGlass, borderSubtle]
  );

  // ===== TEXT COLORS =====
  const text = useMemo(
    () => ({
      primary: textPrimary,
      secondary: textSecondary,
      tertiary: textTertiary,
      inverse: textInverse,
      muted: textMuted,
      // Interactive text
      link: textLink,
      linkHover: textLinkHover,
    }),
    [
      textPrimary,
      textSecondary,
      textTertiary,
      textInverse,
      textMuted,
      textLink,
      textLinkHover,
    ]
  );

  // ===== INTERACTIVE STATES =====
  const interactive = useMemo(
    () => ({
      hover: interactiveHover,
      active: interactiveActive,
      focus: interactiveFocus,
      disabled: interactiveDisabled,
      // Table interactions
      tableHover: interactiveTableHover,
      tableSelected: interactiveTableSelected,
    }),
    [
      interactiveHover,
      interactiveActive,
      interactiveFocus,
      interactiveDisabled,
      interactiveTableHover,
      interactiveTableSelected,
    ]
  );

  // ===== STATUS COLORS =====
  const status = useMemo(
    () => ({
      success: statusSuccess,
      warning: statusWarning,
      error: statusError,
      info: statusInfo,
      // Extended status colors
      successLight: statusSuccessLight,
      warningLight: statusWarningLight,
      errorLight: statusErrorLight,
      infoLight: statusInfoLight,
      successBg: "green/5",
      warningBg: "orange/5",
      errorBg: "red/5",
      infoBg: "blue/5",
      successBorder: statusSuccessBorder,
      warningBorder: statusWarningBorder,
      errorBorder: statusErrorBorder,
      infoBorder: statusInfoBorder,
    }),
    [
      statusSuccess,
      statusWarning,
      statusError,
      statusInfo,
      statusSuccessLight,
      statusWarningLight,
      statusErrorLight,
      statusInfoLight,
      statusSuccessBorder,
      statusWarningBorder,
      statusErrorBorder,
      statusInfoBorder,
    ]
  );

  // ===== SHADOW COLORS =====
  const shadow = useMemo(
    () => ({
      sm: shadowSm,
      md: shadowMd,
      lg: shadowLg,
      // Component-specific shadows
      card: shadowCard,
      cardHover: shadowCardHover,
    }),
    [shadowSm, shadowMd, shadowLg, shadowCard, shadowCardHover]
  );

  // ===== OVERLAY COLORS =====
  const overlay = useMemo(
    () => ({
      backdrop: overlayBackdrop,
      modal: overlayModal,
      // Image overlays
      image: overlayImage,
    }),
    [overlayBackdrop, overlayModal, overlayImage]
  );

  // ===== UTILITY COLORS =====
  const utility = useMemo(
    () => ({
      // Common utility colors used across components
      alt: utilityAlt,
      scroll: utilityScroll,
      scrollBg: utilityScrollBg,
      tableHeader: utilityTableHeader,
      // Input and form colors
      inputBg: utilityInputBg,
      inputBorder: utilityInputBorder,
      // Notification colors
      pulse: utilityPulse,
    }),
    [
      utilityAlt,
      utilityScroll,
      utilityScrollBg,
      utilityTableHeader,
      utilityInputBg,
      utilityInputBorder,
      utilityPulse,
    ]
  );

  return useMemo(
    () => ({
      // Main color sections
      schoolAccent,
      brand,
      background,
      border,
      text,
      interactive,
      status,
      shadow,
      overlay,
      utility,
      logo,
      schoolPlaceholderLogo,
    }),
    [
      schoolAccent,
      brand,
      background,
      border,
      text,
      interactive,
      status,
      shadow,
      overlay,
      utility,
      logo,
    ]
  );
};

export default useThemeColors;
